import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';

import { environment } from '@env';

import { useAuth } from '@/context/AuthProvider';

const ENDPOINT = 'document/google-drive/complete-staged-upload';

export interface CompleteStagedFileUploadRequest {
  stagedDocumentId: string;
  locationId: number;
  folderId?: number;
}

export interface CompleteStagedFileUploadResponse {
  success: boolean;
  message: string;
  documentId?: string;
}

export const useCompleteStagedFileUpload = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CompleteStagedFileUploadRequest) => {
      const response = await axios.post<CompleteStagedFileUploadResponse>(
        `${environment.api}/${ENDPOINT}`,
        request,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      return response.data;
    },
    onSuccess: data => {
      if (data.success) {
        enqueueSnackbar(data.message, { variant: 'success' });

        // Comprehensive query invalidation for real-time updates

        // 1. Invalidate staged files query
        queryClient.invalidateQueries({ queryKey: ['GET', 'document/google-drive/staged-files'] });

        // 2. Invalidate vault documents queries
        queryClient.invalidateQueries({ queryKey: ['GET', 'vault/documents'] });

        // 3. Invalidate all folder-related queries (FolderView component)
        queryClient.invalidateQueries({ queryKey: ['folders'] });

        // 4. Specifically invalidate folder hierarchy queries
        queryClient.invalidateQueries({ queryKey: ['folders', 'hierarchy'] });

        // 5. Invalidate folder contents queries (both root and specific folders)
        queryClient.invalidateQueries({ queryKey: ['folders', 'contents'] });
        queryClient.invalidateQueries({ queryKey: ['folders', 'root-contents'] });

        // 6. Invalidate document categories in case the file affects category counts
        queryClient.invalidateQueries({ queryKey: ['GET', 'vault/document-categories'] });

        // 7. Force refetch of current page data
        queryClient.refetchQueries({ queryKey: ['GET', 'vault/documents'] });
        queryClient.refetchQueries({ queryKey: ['GET', 'document/google-drive/staged-files'] });
      } else {
        enqueueSnackbar(data.message, { variant: 'error' });
      }
    },
    onError: error => {
      enqueueSnackbar('Failed to assign location to file.', { variant: 'error' });
    },
  });
};
